import React, { useState, useMemo } from "react";
import styled from "styled-components";
import {
  getDiffUrl,
  getCurrentUrl,
  hasTargetVariant,
  extractStepMasks,
  isRegressionFirstRun,
  RegressionThresholds,
} from "../utils/regressionEvidence";
import type { TestTarget } from "../services/api";
import type { BaselineUrlMap } from "../hooks/useBaselineUrls";
import { MetricsPanel } from "./MetricsPanel";
import { MaskBadge } from "./MaskBadge";
import {
  ImageCompareModal,
  CompareMode,
} from "./ImageCompareModal";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 4px;
`;

const TabButton = styled.button<{ active?: boolean }>`
  background: ${(p) => (p.active ? "#1976d2" : "#f5f5f5")};
  color: ${(p) => (p.active ? "#fff" : "#424242")};
  border: 1px solid ${(p) => (p.active ? "#1976d2" : "#e0e0e0")};
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const TargetToggle = styled.div`
  display: flex;
  gap: 4px;
`;

const ToggleBtn = styled.button<{ active?: boolean }>`
  background: ${(p) => (p.active ? "#00796b" : "#f5f5f5")};
  color: ${(p) => (p.active ? "#fff" : "#424242")};
  border: 1px solid ${(p) => (p.active ? "#00796b" : "#e0e0e0")};
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
`;

const ImageArea = styled.div`
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  min-height: 200px;
`;

const SideGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const ImgBox = styled.div`
  cursor: zoom-in;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  h5 {
    padding: 4px 8px;
    margin: 0;
    font-size: 11px;
    color: #757575;
    background: #f5f5f5;
  }
  img {
    width: 100%;
    display: block;
  }
  &:focus {
    outline: 2px solid #1976d2;
    outline-offset: 1px;
  }
`;

// 切替モード（インライン）で画像クリックは Baseline/Current の切替に割り当てるため、
// cursor は pointer。拡大は他モード（Side by Side / Diff）からモーダル経由で行う。
// tfact_user_request#215
const OverlayWrap = styled.div`
  position: relative;
  display: inline-block;
  max-width: 100%;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
`;

// 重ね合わせの 2 枚を同サイズ・同位置で重ねるため、
// baseline を絶対配置で重ね、Wrap のサイズは current の自然サイズで決まるようにする。
// 表示中の片方だけを visible にして flicker 比較できる構造にする。
const StackImg = styled.img<{ visible: boolean }>`
  display: block;
  max-width: 100%;
  visibility: ${(p) => (p.visible ? "visible" : "hidden")};
  user-select: none;
  -webkit-user-drag: none;
`;

const StackImgAbsolute = styled.img<{ visible: boolean }>`
  position: absolute;
  inset: 0;
  max-width: 100%;
  visibility: ${(p) => (p.visible ? "visible" : "hidden")};
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 11px;
  color: #757575;
`;

const NoImageMessage = styled.div`
  padding: 20px;
  color: #757575;
  text-align: center;
  font-size: 13px;
`;

// 1 回目実行（ベースラインなし）専用の説明 notice。
// 「動画なし」「ベースライン画像なし」のような誤解を招く表示の代わりに、
// 「これがベースラインになる」ことをユーザーに明示する（issue #189）。
const FirstRunNotice = styled.div`
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 4px;
  padding: 10px 12px;
  color: #0d47a1;
  font-size: 12px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FirstRunNoticeTitle = styled.strong`
  font-size: 13px;
`;

const CurrentOnlyBox = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  cursor: zoom-in;
  h5 {
    padding: 4px 8px;
    margin: 0;
    font-size: 11px;
    color: #757575;
    background: #f5f5f5;
  }
  img {
    width: 100%;
    display: block;
  }
  &:focus {
    outline: 2px solid #1976d2;
    outline-offset: 1px;
  }
`;

// クリック可能な画像ボックスに role="button" / tabIndex / キーボード活性化を付与する
// 共通ハンドラ。Enter / Space で onClick と同等の挙動にする（アクセシビリティ）。
const activateOnKey = (
  e: React.KeyboardEvent<HTMLElement>,
  handler: () => void,
) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    handler();
  }
};

type TargetMode = "full" | "target";
type OverlaySide = "baseline" | "current";

export interface RegressionDiffViewProps {
  stepOrder: number;
  evidenceFiles: Record<string, string>;
  operationStep: { test_target_id: string | null; evidence: string | null } | undefined;
  scoreReason: string | undefined;
  testTargets: TestTarget[];
  allOperationSteps?: { test_target_id: string | null | undefined; evidence: string | null }[];
  baselineUrls?: BaselineUrlMap;
  // controller (#189) が is_regression=True のとき必ず書く marker。
  // 空 dict なら「ベースライン不在の 1 回目実行」と判定し専用 notice を出す。
  regressionBaseline?: Record<string, { full?: string | null; target?: string | null } | null>;
  regressionThresholds?: RegressionThresholds;
}

export const RegressionDiffView: React.FC<RegressionDiffViewProps> = ({
  stepOrder,
  evidenceFiles,
  operationStep,
  scoreReason,
  testTargets,
  allOperationSteps,
  baselineUrls,
  regressionBaseline,
  regressionThresholds,
}) => {
  const firstRun = isRegressionFirstRun(regressionBaseline);
  const [mode, setMode] = useState<CompareMode>("sidebyside");
  const [targetMode, setTargetMode] = useState<TargetMode>("full");
  const [overlaySide, setOverlaySide] = useState<OverlaySide>("baseline");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<CompareMode>("sidebyside");

  const showTargetToggle = hasTargetVariant(evidenceFiles, stepOrder);
  const effectiveIsTarget = showTargetToggle && targetMode === "target";

  // baseline 画像は別 test_result（前回 OK 結果 or testcase 添付）にあるため、
  // controller が解析時に value.regression_baseline へ保存した file_id を
  // 上位 hook (useBaselineUrls) で signed URL に解決し、ここでは map を引くだけ。
  const baselineEntry = baselineUrls?.[String(stepOrder)];
  const baseline = effectiveIsTarget ? baselineEntry?.target : baselineEntry?.full;
  const current = getCurrentUrl(evidenceFiles, operationStep, effectiveIsTarget);
  const diff = getDiffUrl(evidenceFiles, stepOrder, effectiveIsTarget);

  // マスク情報抽出: step 単位ではなく実行全体から集める
  const masks = useMemo(() => {
    const steps = allOperationSteps ?? (operationStep ? [operationStep] : []);
    return extractStepMasks(testTargets, steps);
  }, [testTargets, allOperationSteps, operationStep]);

  const openModal = () => {
    setModalMode(mode);
    setModalOpen(true);
  };

  const renderArea = () => {
    if (mode === "sidebyside") {
      if (firstRun) {
        // 1 回目実行: 比較対象が無いため、今回のキャプチャだけを表示する。
        // 「ベースライン画像なし」よりも「これがベースラインになる」と
        // 前向きに伝えた方が混乱しないので current のみ大きく見せる。
        if (!current) {
          return <NoImageMessage>キャプチャ画像なし</NoImageMessage>;
        }
        return (
          <CurrentOnlyBox
            role="button"
            tabIndex={0}
            aria-label="Current 画像を拡大表示"
            onClick={openModal}
            onKeyDown={(e) => activateOnKey(e, openModal)}
          >
            <h5>Current（次回以降のベースラインとして保存されました）</h5>
            <img src={current} alt="current" />
          </CurrentOnlyBox>
        );
      }
      if (!baseline && !current) {
        return <NoImageMessage>ベースライン画像なし / 現在画像なし</NoImageMessage>;
      }
      if (!baseline) {
        return <NoImageMessage>ベースライン画像なし（添付なし）</NoImageMessage>;
      }
      if (!current) {
        return <NoImageMessage>現在画像なし</NoImageMessage>;
      }
      return (
        <SideGrid>
          <ImgBox
            role="button"
            tabIndex={0}
            aria-label="Baseline 画像を拡大表示"
            onClick={openModal}
            onKeyDown={(e) => activateOnKey(e, openModal)}
          >
            <h5>Baseline</h5>
            <img src={baseline} alt="baseline" />
          </ImgBox>
          <ImgBox
            role="button"
            tabIndex={0}
            aria-label="Current 画像を拡大表示"
            onClick={openModal}
            onKeyDown={(e) => activateOnKey(e, openModal)}
          >
            <h5>Current</h5>
            <img src={current} alt="current" />
          </ImgBox>
        </SideGrid>
      );
    }
    if (mode === "diff") {
      if (!diff) {
        return <NoImageMessage>差分画像なし（ベースライン一致 or 未生成）</NoImageMessage>;
      }
      return (
        <ImgBox
          role="button"
          tabIndex={0}
          aria-label="Diff 画像を拡大表示"
          onClick={openModal}
          onKeyDown={(e) => activateOnKey(e, openModal)}
        >
          <h5>Diff Heatmap</h5>
          <img src={diff} alt="diff" />
        </ImgBox>
      );
    }
    // 切替（旧 overlay）: baseline と current を同位置に重ね、表示する側を切り替える（flicker 比較）。
    // ブレンドではなく完全切替にすることで、わずかなピクセル差も「目が動かずに」検出できる。
    // tfact_user_request#215: 画像のシングルクリックは Baseline/Current の切替に割り当てる
    //（ボタン操作と同等）。拡大が必要なら Side by Side / Diff から開けるモーダル経由で見る。
    if (!baseline || !current) {
      return <NoImageMessage>切替には baseline と current 両方が必要</NoImageMessage>;
    }
    const showBaseline = overlaySide === "baseline";
    const toggleSide = () => setOverlaySide(showBaseline ? "current" : "baseline");
    return (
      <>
        <ToggleRow>
          表示:
          <ToggleBtn
            active={showBaseline}
            onClick={(e) => { e.stopPropagation(); setOverlaySide("baseline"); }}
          >Baseline</ToggleBtn>
          <ToggleBtn
            active={!showBaseline}
            onClick={(e) => { e.stopPropagation(); setOverlaySide("current"); }}
          >Current</ToggleBtn>
          <span style={{ marginLeft: 8, color: "#9e9e9e" }}>
            ボタンまたは画像クリックで Baseline / Current を切替
          </span>
        </ToggleRow>
        <OverlayWrap
          role="button"
          tabIndex={0}
          aria-label="Baseline / Current を切替"
          onClick={toggleSide}
          onKeyDown={(e) => activateOnKey(e, toggleSide)}
          title="クリックで Baseline / Current を切替"
        >
          {/* current を非絶対配置にして Wrap の box サイズを決める。
              baseline は absolute で同位置に重ねる。両者ほぼ同寸法の前提。 */}
          <StackImg src={current} alt="current" visible={!showBaseline} />
          <StackImgAbsolute src={baseline} alt="baseline" visible={showBaseline} />
        </OverlayWrap>
      </>
    );
  };

  return (
    <Container>
      {firstRun && (
        <FirstRunNotice>
          <FirstRunNoticeTitle>
            ベースライン作成（リグレッション 1 回目実行）
          </FirstRunNoticeTitle>
          <span>
            このテストケースには比較対象のベースライン画像が存在しないため、画像比較は実施されていません。
            今回のキャプチャが次回以降の比較ベースラインとして保存されました。
          </span>
        </FirstRunNotice>
      )}
      {/* 1 回目実行（first-run）では baseline / diff が存在しないため
          「Side by Side / Diff / 切替」タブを表示しても全 disabled で混乱を招くだけ。
          notice + Current 単体表示に集中させ、TopBar は MaskBadge のみ残す（issue #192）。 */}
      <TopBar>
        {!firstRun && (
          <TabRow>
            <TabButton
              active={mode === "sidebyside"}
              disabled={!baseline || !current}
              onClick={() => setMode("sidebyside")}
            >Side by Side</TabButton>
            <TabButton
              active={mode === "diff"}
              disabled={!diff}
              onClick={() => setMode("diff")}
            >Diff</TabButton>
            <TabButton
              active={mode === "overlay"}
              disabled={!baseline || !current}
              onClick={() => setMode("overlay")}
            >切替</TabButton>
          </TabRow>
        )}
        {firstRun && <span />}
        <MaskBadge masks={masks} />
      </TopBar>

      {showTargetToggle && (
        <TargetToggle>
          <ToggleBtn active={targetMode === "full"} onClick={() => setTargetMode("full")}>Full</ToggleBtn>
          <ToggleBtn active={targetMode === "target"} onClick={() => setTargetMode("target")}>Target周辺</ToggleBtn>
        </TargetToggle>
      )}

      <ImageArea>{renderArea()}</ImageArea>
      {/* first-run では MS-SSIM スコアが存在せず、MetricsPanel は「スコア情報なし」しか
          出せないため、上の FirstRunNotice と内容重複になる。冗長な表示を避ける。 */}
      {!firstRun && (
        <MetricsPanel scoreReason={scoreReason} thresholds={regressionThresholds} />
      )}

      <ImageCompareModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        onModeChange={setModalMode}
        baseline={baseline}
        current={current}
        diff={diff}
      />
    </Container>
  );
};
